import { lazy } from "react";
import { L, Navigate, type RouteDef } from "./shared";

// Service / strategy pages (lazy)
const B2BConsulting = lazy(() => import("@/pages/B2BConsulting"));
const WealthPreservation = lazy(() => import("@/pages/WealthPreservation"));
const BusinessSuccession = lazy(() => import("@/pages/BusinessSuccession"));
const ExecutiveBenefits = lazy(() => import("@/pages/ExecutiveBenefits"));
const RetirementPlanning = lazy(() => import("@/pages/RetirementPlanning"));
const EstatePlanning = lazy(() => import("@/pages/EstatePlanning"));
const BuySellAgreements = lazy(() => import("@/pages/BuySellAgreements"));
const BusinessSuccessionArizona = lazy(
  () => import("@/pages/BusinessSuccessionArizona"),
);
const RolloverEducation = lazy(() => import("@/pages/RolloverEducation"));
const KeyPersonInsurance = lazy(() => import("@/pages/KeyPersonInsurance"));
const CommercialFunding = lazy(() => import("@/pages/CommercialFunding"));
const BusinessOwnerFinancialStrategyAZ = lazy(
  () => import("@/pages/BusinessOwnerFinancialStrategyAZ"),
);

// Industries
const ConstructionStrategy = lazy(
  () => import("@/pages/industries/ConstructionStrategy"),
);
const ManufacturingStrategy = lazy(
  () => import("@/pages/industries/ManufacturingStrategy"),
);
const HealthcareStrategy = lazy(
  () => import("@/pages/industries/HealthcareStrategy"),
);
const DentalStrategy = lazy(() => import("@/pages/industries/DentalStrategy"));
const LogisticsStrategy = lazy(
  () => import("@/pages/industries/LogisticsStrategy"),
);
const RealEstateInvestors = lazy(
  () => import("@/pages/industries/RealEstateInvestors"),
);

// Multi-State Service Pages
const BusinessSuccessionNevada = lazy(
  () => import("@/pages/states/BusinessSuccessionNevada"),
);
const BusinessSuccessionColorado = lazy(
  () => import("@/pages/states/BusinessSuccessionColorado"),
);
const BusinessSuccessionUtah = lazy(
  () => import("@/pages/states/BusinessSuccessionUtah"),
);
const BusinessSuccessionWashington = lazy(
  () => import("@/pages/states/BusinessSuccessionWashington"),
);
const BusinessSuccessionFlorida = lazy(
  () => import("@/pages/states/BusinessSuccessionFlorida"),
);
const BusinessSuccessionTexas = lazy(
  () => import("@/pages/states/BusinessSuccessionTexas"),
);

export const serviceRoutes: RouteDef[] = [
  {
    path: "/business-owner-financial-strategy",
    element: (
      <L>
        <BusinessOwnerFinancialStrategyAZ />
      </L>
    ),
  },
  {
    path: "/business-succession-planning",
    element: (
      <L>
        <BusinessSuccession />
      </L>
    ),
  },
  {
    path: "/buy-sell-agreement-funding",
    element: (
      <L>
        <BuySellAgreements />
      </L>
    ),
  },
  {
    path: "/key-person-risk-protection",
    element: (
      <L>
        <KeyPersonInsurance />
      </L>
    ),
  },
  {
    path: "/executive-retention-planning",
    element: (
      <L>
        <ExecutiveBenefits />
      </L>
    ),
  },
  {
    path: "/business-owner-trust-coordination",
    element: (
      <L>
        <EstatePlanning />
      </L>
    ),
  },
  {
    path: "/business-owner-retirement-planning",
    element: (
      <L>
        <RetirementPlanning />
      </L>
    ),
  },
  {
    path: "/commercial-capital-solutions",
    element: (
      <L>
        <CommercialFunding />
      </L>
    ),
  },
  {
    path: "/401k-rollover-education",
    element: (
      <L>
        <RolloverEducation />
      </L>
    ),
  },
  {
    path: "/wealth-preservation-strategy",
    element: (
      <L>
        <WealthPreservation />
      </L>
    ),
  },
  {
    path: "/where-srg-fits",
    element: (
      <L>
        <B2BConsulting />
      </L>
    ),
  },
  {
    path: "/business-exit-planning",
    element: (
      <L>
        <BusinessSuccession />
      </L>
    ),
  },
  {
    path: "/industries/construction",
    element: (
      <L>
        <ConstructionStrategy />
      </L>
    ),
  },
  {
    path: "/industries/manufacturing",
    element: (
      <L>
        <ManufacturingStrategy />
      </L>
    ),
  },
  {
    path: "/industries/healthcare",
    element: (
      <L>
        <HealthcareStrategy />
      </L>
    ),
  },
  {
    path: "/industries/dental",
    element: (
      <L>
        <DentalStrategy />
      </L>
    ),
  },
  {
    path: "/industries/logistics",
    element: (
      <L>
        <LogisticsStrategy />
      </L>
    ),
  },
  {
    path: "/industries/real-estate-investors",
    element: (
      <L>
        <RealEstateInvestors />
      </L>
    ),
  },
  // Multi-State Service Pages — canonical /locations/ URLs
  {
    path: "/locations/arizona",
    element: (
      <L>
        <BusinessSuccessionArizona />
      </L>
    ),
  },
  {
    path: "/locations/nevada",
    element: (
      <L>
        <BusinessSuccessionNevada />
      </L>
    ),
  },
  {
    path: "/locations/colorado",
    element: (
      <L>
        <BusinessSuccessionColorado />
      </L>
    ),
  },
  {
    path: "/locations/utah",
    element: (
      <L>
        <BusinessSuccessionUtah />
      </L>
    ),
  },
  {
    path: "/locations/washington",
    element: (
      <L>
        <BusinessSuccessionWashington />
      </L>
    ),
  },
  {
    path: "/locations/florida",
    element: (
      <L>
        <BusinessSuccessionFlorida />
      </L>
    ),
  },
  {
    path: "/locations/texas",
    element: (
      <L>
        <BusinessSuccessionTexas />
      </L>
    ),
  },
  // Legacy state URLs (redirects to canonical /locations/ URLs)
  {
    path: "/business-succession-nevada",
    element: <Navigate to="/locations/nevada" replace />,
  },
  {
    path: "/business-succession-colorado",
    element: <Navigate to="/locations/colorado" replace />,
  },
  {
    path: "/business-succession-utah",
    element: <Navigate to="/locations/utah" replace />,
  },
  {
    path: "/business-succession-washington",
    element: <Navigate to="/locations/washington" replace />,
  },
  {
    path: "/business-succession-florida",
    element: <Navigate to="/locations/florida" replace />,
  },
  {
    path: "/business-succession-texas",
    element: <Navigate to="/locations/texas" replace />,
  },
];
